import type { CSSProperties } from "react";

import { FRAME } from "../screen";
import {
  ORB_GLOW_OPACITY,
  ORB_GLOW_STOPS,
  ORB_RED,
  ORB_RED_OPACITY,
  ORBS,
  WAVE_COLUMNS,
  WAVE_PATH,
  WAVE_ROWS,
  waveColor,
} from "./geometry";
import styles from "./Backdrop.module.css";

const size = {
  "--frame-width": FRAME.width,
  "--frame-height": FRAME.height,
} as CSSProperties;

/** Where each sampled gradient sits, as a fraction of the frame. */
const at = (index: number, count: number) => index / (count - 1);

const COLUMNS = Array.from({ length: WAVE_COLUMNS }, (_, index) => at(index, WAVE_COLUMNS));
const ROWS = Array.from({ length: WAVE_ROWS }, (_, index) => at(index, WAVE_ROWS));

/** Column 0 is the base coat; the rest fade in over the column before them. */
const OVERLAID = COLUMNS.slice(1).map((column, index) => ({ column, from: COLUMNS[index] }));

/**
 * Figma's "Background" (36:62): the wave, and the two orbs that sit under it.
 *
 * The mesh gradient the wave is painted with has no CSS or SVG equivalent, so
 * `waveColor` is sampled into one vertical gradient per column of the sampling
 * grid, and those are cross-faded across — which is the bilinear reading of a
 * surface that is otherwise only available a pixel at a time.
 */
export function Backdrop() {
  return (
    <svg
      className={styles.backdrop}
      style={size}
      data-backdrop=""
      viewBox={`0 0 ${FRAME.width} ${FRAME.height}`}
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        {COLUMNS.map((column) => (
          <linearGradient
            key={column}
            id={`easter-egg-wave-${column}`}
            gradientUnits="userSpaceOnUse"
            x1={0}
            y1={0}
            x2={0}
            y2={FRAME.height}
          >
            {ROWS.map((row) => (
              <stop key={row} offset={row} stopColor={waveColor(column, row)} />
            ))}
          </linearGradient>
        ))}

        {OVERLAID.map(({ column, from }) => (
          <linearGradient
            key={column}
            id={`easter-egg-wave-fade-${column}`}
            gradientUnits="userSpaceOnUse"
            x1={0}
            y1={0}
            x2={FRAME.width}
            y2={0}
          >
            <stop offset={from} stopColor="#fff" stopOpacity={0} />
            <stop offset={column} stopColor="#fff" stopOpacity={1} />
          </linearGradient>
        ))}

        {OVERLAID.map(({ column }) => (
          <mask
            key={column}
            id={`easter-egg-wave-mask-${column}`}
            maskUnits="userSpaceOnUse"
            x={0}
            y={0}
            width={FRAME.width}
            height={FRAME.height}
          >
            <rect
              width={FRAME.width}
              height={FRAME.height}
              fill={`url(#easter-egg-wave-fade-${column})`}
            />
          </mask>
        ))}

        <clipPath id="easter-egg-wave">
          <path d={WAVE_PATH} />
        </clipPath>

        {ORBS.map((orb) => (
          <radialGradient
            key={orb.id}
            id={`easter-egg-orb-glow-${orb.id}`}
            gradientUnits="userSpaceOnUse"
            cx={orb.glow.cx}
            cy={orb.glow.cy}
            r={orb.glow.r}
          >
            {ORB_GLOW_STOPS.map((stop) => (
              <stop
                key={stop.offset}
                offset={stop.offset}
                stopColor={stop.color}
                stopOpacity={stop.opacity}
              />
            ))}
          </radialGradient>
        ))}
      </defs>

      {/* The orbs sit under the wave: where the wave paints, it covers them. */}
      {ORBS.map((orb) => (
        <g key={orb.id}>
          <circle cx={orb.cx} cy={orb.cy} r={orb.r} fill={ORB_RED} fillOpacity={ORB_RED_OPACITY} />
          <circle
            cx={orb.cx}
            cy={orb.cy}
            r={orb.r}
            fill={`url(#easter-egg-orb-glow-${orb.id})`}
            fillOpacity={ORB_GLOW_OPACITY}
          />
        </g>
      ))}

      <g clipPath="url(#easter-egg-wave)">
        <rect
          width={FRAME.width}
          height={FRAME.height}
          fill={`url(#easter-egg-wave-${COLUMNS[0]})`}
        />
        {OVERLAID.map(({ column }) => (
          <rect
            key={column}
            width={FRAME.width}
            height={FRAME.height}
            fill={`url(#easter-egg-wave-${column})`}
            mask={`url(#easter-egg-wave-mask-${column})`}
          />
        ))}
      </g>
    </svg>
  );
}
